import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { providerAPI, pingAPI } from '@/api';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Globe, MessageCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const ProviderProfile = () => {
  const { userId } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProvider();
  }, [userId]);

  const fetchProvider = async () => {
    try {
      const response = await providerAPI.getProvider(userId);
      setProvider(response.data);
    } catch (error) {
      toast.error('Failed to load provider details');
      navigate('/seeker/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInterest = async () => {
    if (!user) {
      toast.error('Please login to express interest');
      navigate('/auth');
      return;
    }

    if (user.role !== 'seeker') {
      toast.error('Only seekers can express interest');
      return;
    }

    setSending(true);
    try {
      await pingAPI.createPing(userId);
      toast.success('Interest sent! The provider will contact you.');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already expressed interest in this provider');
      } else {
        toast.error('Failed to send interest');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-[#4B5563]">Loading provider details...</p>
        </div>
      </div>
    );
  }

  if (!provider) return null;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-border/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-[#4B5563] hover:text-primary hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      {/* Profile Content */}
      <div className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Profile Header */}
          <div className="bg-gradient-to-b from-secondary to-white rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-24 h-24 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-4xl font-heading font-bold text-primary">
                  {provider.user.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#2C3333] mb-2">
                  {provider.user.name}
                </h1>
                {provider.profile.clinic_name && (
                  <p className="text-lg text-[#4B5563] mb-3">{provider.profile.clinic_name}</p>
                )}
                <div className="flex flex-wrap gap-4 text-[#4B5563]">
                  <span className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    {provider.profile.experience} years of experience
                  </span>
                  <span className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    {provider.profile.city}
                  </span>
                  <span className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    {provider.profile.session_type}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* About */}
              <div className="bg-white rounded-2xl border border-border/40 p-6">
                <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">About</h2>
                <p className="text-[#4B5563] leading-relaxed">{provider.profile.bio}</p>
              </div>

              {/* Specializations */}
              <div className="bg-white rounded-2xl border border-border/40 p-6">
                <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Areas of Specialization</h2>
                <div className="flex flex-wrap gap-3">
                  {provider.profile.specializations.map((spec) => (
                    <span
                      key={spec}
                      className="bg-secondary text-primary px-4 py-2 rounded-full text-sm font-medium tracking-wide uppercase"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div className="bg-white rounded-2xl border border-border/40 p-6">
                <h2 className="font-heading text-2xl font-semibold text-[#2C3333] mb-4">Languages Spoken</h2>
                <div className="flex flex-wrap gap-3">
                  {provider.profile.languages.map((lang) => (
                    <span
                      key={lang}
                      className="bg-[#F9FAFB] text-[#2C3333] px-4 py-2 rounded-lg text-sm font-medium"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-primary to-primary-hover rounded-2xl p-6 text-white sticky top-24">
                <h3 className="font-heading text-xl font-semibold mb-2">Get in Touch</h3>
                <p className="text-white/90 text-sm mb-6 leading-relaxed">
                  Express your interest and the provider will reach out to you directly.
                </p>
                
                {user?.role === 'seeker' ? (
                  <Button
                    data-testid="interest-btn"
                    onClick={handleInterest}
                    disabled={sending}
                    className="w-full bg-accent hover:bg-accent-hover text-white rounded-full py-6 font-medium transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {sending ? 'Sending...' : "I'm Interested"}
                  </Button>
                ) : (
                  <Button
                    data-testid="login-to-interest-btn"
                    onClick={() => navigate('/auth')}
                    className="w-full bg-accent hover:bg-accent-hover text-white rounded-full py-6 font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Login to Express Interest
                  </Button>
                )}
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl border border-border/40 p-6">
                <h3 className="font-heading text-lg font-semibold text-[#2C3333] mb-3">Location</h3>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <p className="text-[#4B5563] text-sm leading-relaxed">{provider.profile.address}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfile;